import { useCallback, useMemo, useState } from 'react';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { Chip, IconButton, List, ListItem, ListItemText, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { UserSession } from 'api';
import { ConfirmationDialog } from 'components/Dialogs';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import useAccountSettingsNavItems from 'pages/UserProfile/useAccountSettingsNavItems';
import { formatLastUsed } from 'utilities';
import { useUserSessions } from './hooks';
import {
  CURRENT_SESSION_LABEL,
  EMPTY_MESSAGE,
  LOAD_ERROR_MESSAGE,
  PAGE_DESCRIPTION,
  PAGE_TITLE,
  REVOKE_DIALOG_MESSAGE,
  REVOKE_DIALOG_TITLE,
  UNKNOWN_DEVICE_LABEL,
} from './UserSessions.constants';
import { USER_SESSIONS_TEST_IDS } from './UserSessions.testIds';
import { RevokeConfirmationState } from './UserSessions.types';

const describeSession = (session: UserSession) =>
  [session.user_agent || UNKNOWN_DEVICE_LABEL, session.ip_address].filter(Boolean).join(' · ');

const UserSessions = () => {
  const accountSettingsNavItems = useAccountSettingsNavItems();
  const { isError, isLoading, isRevoking, revokeSession, sessions } = useUserSessions();
  const [confirmation, setConfirmation] = useState<RevokeConfirmationState>({
    open: false,
    sessionId: null,
  });

  const secondaryNavigation = useMemo(
    () => ({ title: 'Account', items: accountSettingsNavItems }),
    [accountSettingsNavItems]
  );
  useSecondaryNavigationRegistration(secondaryNavigation);

  const closeConfirmation = useCallback(() => {
    setConfirmation({ open: false, sessionId: null });
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    if (!confirmation.sessionId) {
      return;
    }

    await revokeSession(confirmation.sessionId);
    closeConfirmation();
  }, [closeConfirmation, confirmation.sessionId, revokeSession]);

  const renderBody = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" height={120} />;
    }

    if (sessions.length) {
      return (
        <Paper variant="outlined">
          <List disablePadding data-testid={USER_SESSIONS_TEST_IDS.list}>
            {sessions.map((session, index) => (
              <ListItem
                key={session.id}
                divider={index < sessions.length - 1}
                data-testid={USER_SESSIONS_TEST_IDS.row(String(session.id))}
                secondaryAction={
                  session.is_current ? null : (
                    <IconButton
                      edge="end"
                      aria-label={`Sign out ${describeSession(session)}`}
                      disabled={isRevoking}
                      onClick={() => setConfirmation({ open: true, sessionId: String(session.id) })}
                      data-testid={USER_SESSIONS_TEST_IDS.revokeButton(String(session.id))}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  )
                }>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{describeSession(session)}</span>
                      {session.is_current ? (
                        <Chip
                          size="small"
                          label={CURRENT_SESSION_LABEL}
                          data-testid={USER_SESSIONS_TEST_IDS.currentBadge}
                        />
                      ) : null}
                    </Stack>
                  }
                  secondary={`Last used: ${formatLastUsed(session.last_used_at ?? null)}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      );
    }

    // Checked after the list above so a background refetch failure (e.g. after
    // revoking a session) can't blank out a session list we already have.
    if (isError) {
      return (
        <Typography variant="body2" color="error" data-testid={USER_SESSIONS_TEST_IDS.errorState}>
          {LOAD_ERROR_MESSAGE}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary" data-testid={USER_SESSIONS_TEST_IDS.emptyState}>
        {EMPTY_MESSAGE}
      </Typography>
    );
  };

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack spacing={0.5}>
        <Typography variant="h6">{PAGE_TITLE}</Typography>
        <Typography variant="body2" color="text.secondary">
          {PAGE_DESCRIPTION}
        </Typography>
      </Stack>

      {renderBody()}

      <ConfirmationDialog
        open={confirmation.open}
        title={REVOKE_DIALOG_TITLE}
        onClose={closeConfirmation}
        isLoading={isRevoking}
        actions={[
          { label: 'Cancel', variant: 'text', callback: closeConfirmation },
          {
            label: 'Sign out',
            variant: 'contained',
            type: 'danger',
            callback: handleConfirmRevoke,
          },
        ]}>
        <Typography>{REVOKE_DIALOG_MESSAGE}</Typography>
      </ConfirmationDialog>
    </Stack>
  );
};

export default UserSessions;
